// =============================================================================
//  src/minigames/DocPlayer.jsx
//  EL VÍDEO CON EJERCICIOS, TAL Y COMO LO JUEGA EL ALUMNO
//
//  Pausa el vídeo en `trigger_seconds` —después de que el segmento ya se haya
//  escuchado— y enseña el ejercicio. Los seis tipos los pinta `ExerciseWidget`.
//
//  ── LO QUE CAMBIÓ EL 06-10-2026 ─────────────────────────────────────────────
//  Rob, jugando: «a veces cometo errores y quiero leer con calma los feedbacks,
//  pero cuando estoy leyendo se quita en automático y sigue el vídeo»; «no
//  tenemos una barra de progreso del vídeo, no sabemos cómo vamos»; «Próxima
//  pregunta en 3:51 no me dice nada»; «✅ 4/5 correctas esto también está
//  desconectado».
//
//  Las cuatro eran ciertas y las cuatro eran el mismo problema de fondo: la
//  pantalla enseñaba números que no venían de lo que estaba pasando.
//
//    · La explicación se iba sola a los 1,4 s (acierto) o 1,8 s (fallo). Un
//      reloj no sabe cuándo has terminado de leer. Ahora el vídeo espera a que
//      pulses «Continuar», que es la única señal que de verdad lo dice.
//    · «Próxima pregunta en 3:51» no era un «en», era el minuto del vídeo en el
//      que caía la siguiente, escrito con las palabras de una cuenta atrás.
//      Ahora es una cuenta atrás de verdad.
//    · El marcador contaba como acierto TODAS las parejas, acertaras o no: ese
//      tipo de ejercicio nunca avisaba de un fallo. Ahora cada pregunta guarda
//      su resultado y los números salen de ahí.
//    · Y no había forma de ver por dónde vas. La barra de abajo enseña el vídeo
//      entero con una marca por ejercicio, y las marcas se pintan según cómo
//      haya ido cada uno.
// =============================================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { alternarPantallaCompleta, hayPantallaCompleta } from './pantallaCompleta';
import { useYouTubePlayer } from './useYouTubePlayer';
import ExerciseWidget from './ExerciseWidget';

const FLAME_INIT = 50;
const FLAME_HIT = 15;
const FLAME_MISS = -20;

export const reloj = (segundos) => {
    const s = Math.max(0, Math.floor(Number(segundos) || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const r = s % 60;
    return h > 0
        ? `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`
        : `${m}:${String(r).padStart(2, '0')}`;
};

//  ── EL TEXTO EN PANTALLA (CC) · 14-10-2026 ─────────────────────────────────
//  Rob: «los básicos batallan al oír; ¿es buena idea ponerles el texto, o sólo
//  estamos haciendo más flojo al alumno?».
//
//  Se pone, con tres frenos que son los que hacen que ayude en vez de sustituir:
//
//   1. **Sólo el texto del propio vídeo.** Rob: «nada, pero nada en español».
//      Aquí no se traduce ni una palabra: es el transcript que se oye. En
//      español el alumno leería en vez de escuchar.
//   2. **Apagado de entrada, y se acepta antes de encenderlo.** Se le dice para
//      quién es y para quién no, y decide él.
//   3. **DESAPARECE cuando salta la pregunta.** Éste es el freno de verdad: el
//      texto acompaña mientras escucha, pero a la hora de contestar no está,
//      así que no se puede copiar la respuesta del renglón y el ejercicio sigue
//      midiendo lo que oyó.
const CC_PUESTO = 'nsl_cc_video';
const CC_ACEPTADO = 'nsl_cc_video_aceptado';

const leeAjuste = (clave) => {
    //  En ventana privada `localStorage` lanza al leerlo. Un ajuste que no se
    //  puede recordar no puede tirar el reproductor entero.
    try { return localStorage.getItem(clave); } catch { return null; }
};
const guardaAjuste = (clave, valor) => {
    try { localStorage.setItem(clave, valor); } catch { /* ventana privada */ }
};

/**
 * Desde dónde se vuelve a escuchar la pregunta `idx`.
 *
 * Rob, 14-10-2026: «repetí para volver a oír y no se repitió el vídeo justo
 * después del disparo anterior; al parecer sólo se regresó -0.5 s. Si el alumno
 * quiere volver a escuchar debe ser antes del disparo anterior, y si es la
 * primera pregunta debe regresar hasta el principio del vídeo».
 *
 * Tenía razón y mi primera versión partía de una idea equivocada: volvía al
 * principio del FRAGMENTO de esa pregunta, que es un renglón de transcript —tres
 * o cuatro segundos, a veces medio—. Eso no es volver a escuchar: es repetir la
 * última palabra.
 *
 * Lo que el alumno necesita oír otra vez es **todo lo que ha oído desde la
 * última pausa**, porque es exactamente el trozo del que se le está preguntando.
 * O sea: desde el disparo anterior, y desde el 0:00 si es la primera.
 *
 * El anterior DISTINTO: varias preguntas de un mismo momento comparten disparo,
 * y volver al suyo sería quedarse donde está.
 */
export function desdeDondeSeRepite(questions, idx) {
    const lista = Array.isArray(questions) ? questions : [];
    const q = lista[idx];
    if (!q) return null;
    const suyo = Number(q.trigger_seconds);
    if (!Number.isFinite(suyo)) return null;
    let anterior = 0;
    for (const otra of lista) {
        const t = Number(otra?.trigger_seconds);
        if (Number.isFinite(t) && t < suyo && t > anterior) anterior = t;
    }
    return anterior;
}

/** Qué renglón del transcript toca en este segundo. */
export function renglonEn(captions, t) {
    const lista = Array.isArray(captions) ? captions : [];
    const s = Number(t) || 0;
    for (const c of lista) {
        if (s >= Number(c?.start) && s < Number(c?.end)) return c;
    }
    return null;
}

export default function DocPlayer({ token, video, onFinish, onBack }) {
    const questions = video.planned_questions || [];
    const captions = video.captions || [];

    const [qIdx, setQIdx] = useState(-1);   // -1 = ninguna activa
    const [nextTrigger, setNext] = useState(questions[0]?.trigger_seconds ?? null);
    const [answering, setAnswering] = useState(false);
    const [flame, setFlame] = useState(FLAME_INIT);
    const [effect, setEffect] = useState(null);
    const [finished, setFinished] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);

    //  ── LOS NÚMEROS SALEN DE AQUÍ ─────────────────────────────────────
    //  Un hueco por pregunta: `true` acertada, `false` fallada, `undefined`
    //  todavía sin contestar. Antes eran dos contadores sueltos (`correct` y
    //  `total`) que cada tipo de ejercicio incrementaba por su cuenta — y el de
    //  parejas no incrementaba el de fallos NUNCA. Con un hueco por pregunta no
    //  hay forma de que el marcador y los círculos digan cosas distintas:
    //  los dos leen esto.
    const [resultados, setResultados] = useState(() => questions.map(() => undefined));
    //  Cuando una pregunta ya está contestada pero el alumno sigue leyendo.
    const [leyendo, setLeyendo] = useState(false);

    //  El reloj del vídeo, para la barra y para la cuenta atrás.
    const [tiempo, setTiempo] = useState(0);
    const [duracion, setDuracion] = useState(0);

    //  ── EL TEXTO EN PANTALLA ──────────────────────────────────────────
    //  Apagado de entrada SIEMPRE que no se haya encendido antes. Ver la nota
    //  de arriba: lo que lo hace una rampa y no una silla de ruedas es que se
    //  elige, no que venga puesto.
    const [cc, setCc] = useState(() => leeAjuste(CC_PUESTO) === '1');
    const [pidiendoCc, setPidiendoCc] = useState(false);

    //  ── REPETIR EL TROZO, UNA VEZ ─────────────────────────────────────
    //  Rob: «cuando llegan a la pregunta y no les queda claro, deberíamos tener
    //  una oportunidad de repetir esa parte del vídeo donde no entendieron. Si
    //  repiten y llegan a la pregunta, ya no pueden repetir otra vez y deben
    //  contestar».
    //
    //  Una por pregunta. Sin el límite, repetir deja de ser una segunda
    //  oportunidad y pasa a ser un bucle hasta que suene la palabra que falta,
    //  que no es escuchar: es buscar.
    const [repetidas, setRepetidas] = useState(() => new Set());
    const [repitiendo, setRepitiendo] = useState(false);
    //  Qué pregunta se está repitiendo y hasta dónde. Es un `ref` porque lo lee
    //  el reloj, que corre cada 150 ms fuera del ciclo de React.
    const repiteRef = useRef(null);

    const { playerRef, playerReadyRef, createPlayer, destroyPlayer } = useYouTubePlayer();
    const pollRef = useRef(null);
    const answeringRef = useRef(false);
    const nextRef = useRef(nextTrigger);
    const qIdxRef = useRef(-1);

    useEffect(() => { answeringRef.current = answering; }, [answering]);
    useEffect(() => { nextRef.current = nextTrigger; }, [nextTrigger]);
    useEffect(() => { qIdxRef.current = qIdx; }, [qIdx]);

    useEffect(() => {
        createPlayer('doc-yt-player', video.youtube_id, () => {
            startPoll();
        });
        return () => { destroyPlayer(); if (pollRef.current) clearInterval(pollRef.current); };
    }, []);

    const startPoll = useCallback(() => {
        pollRef.current = setInterval(() => {
            if (!playerRef.current) return;
            try {
                const state = playerRef.current.getPlayerState();
                setIsPlaying(state === 1 || state === 3);

                //  El reloj se lee SIEMPRE, también con el ejercicio abierto.
                //  Antes esta función se iba por donde venía mientras había una
                //  pregunta en pantalla, así que la barra de progreso se habría
                //  quedado congelada justo cuando el alumno mira el tiempo.
                //  Se redondea a segundos para no repintar seis veces por
                //  segundo sin que nada cambie a la vista.
                const t = playerRef.current.getCurrentTime?.() ?? 0;
                setTiempo(prev => (Math.floor(t) !== Math.floor(prev) ? t : prev));
                const d = playerRef.current.getDuration?.() ?? 0;
                if (d > 0) setDuracion(prev => (prev === d ? prev : d));

                //  ── LA VUELTA DE UNA REPETICIÓN ──────────────────────
                //  Mientras se repite el trozo, la pregunta sigue «abierta»
                //  aunque no se vea: lo que hay que hacer al llegar al disparo
                //  no es buscar la siguiente, es devolver ÉSTA. Por eso va
                //  antes que todo lo demás y corta aquí.
                //
                //  El `return` es un cinturón, y conviene decirlo: hoy, cuando
                //  se repite, `nextRef` ya está a null —lo vació la pregunta al
                //  dispararse—, así que quitarlo no cambiaría nada. Se queda
                //  para que esta rama no dependa de eso.
                if (repiteRef.current) {
                    const r = repiteRef.current;
                    //  ── EL SALTO DE YOUTUBE NO ES INMEDIATO ──────────────
                    //  Rob, 14-10-2026: «cuando son dos ejercicios o más en un
                    //  fragmento y le doy en repetir desde el primero, me sale
                    //  la siguiente pregunta y se rompe la magia». Y antes:
                    //  «solo se regresó -.5s».
                    //
                    //  Era lo mismo las dos veces. Durante un par de décimas
                    //  después de `seekTo`, `getCurrentTime()` sigue devolviendo
                    //  el tiempo de ANTES —el del disparo—, así que el primer
                    //  latido veía `t >= hasta` y abortaba la repetición sin
                    //  haber empezado. Y con el `playVideo` ya lanzado, el
                    //  `pauseVideo` perdía la carrera: el vídeo seguía corriendo
                    //  DETRÁS de la pregunta, y al continuar ya estaba pasado el
                    //  disparo siguiente, que saltaba en el acto.
                    //
                    //  Así que primero se espera a que el reloj aterrice.
                    if (!r.llego) {
                        if (t < r.hasta - 0.5) { r.llego = true; return; }
                        //  Y si no aterriza nunca —el salto se perdió— se
                        //  devuelve la pregunta en vez de dejarla escondida
                        //  para siempre.
                        if (Date.now() - r.desde > 4000) {
                            playerRef.current.pauseVideo();
                            repiteRef.current = null;
                            setRepitiendo(false);
                            answeringRef.current = true;
                            setAnswering(true);
                        }
                        return;
                    }
                    if (t >= r.hasta) {
                        playerRef.current.pauseVideo();
                        repiteRef.current = null;
                        setRepitiendo(false);
                        answeringRef.current = true;
                        setAnswering(true);
                    }
                    return;
                }

                if (answeringRef.current) return;
                if (nextRef.current === null) return;
                if (state !== 1) return;
                if (t >= nextRef.current) {
                    playerRef.current.pauseVideo();
                    const idx = questions.findIndex(q => q.trigger_seconds === nextRef.current);
                    answeringRef.current = true;
                    setQIdx(idx);
                    setAnswering(true);
                    setLeyendo(false);
                    setNext(null); // evita re-trigger
                    nextRef.current = null;
                }
            } catch (_) { }
        }, 150);
    }, [playerRef, questions]);

    /**
     * El alumno acaba de resolver la pregunta que está en pantalla.
     *
     * Aquí NO se reanuda el vídeo. Se guarda el resultado y se deja la
     * explicación puesta; quien decide cuándo seguir es él, con «Continuar».
     */
    const alResolver = (acierto) => {
        const i = qIdxRef.current;
        setResultados(prev => {
            //  Si ya estaba contestada no se pisa: un doble clic no puede
            //  convertir un fallo en acierto ni al revés.
            if (i < 0 || prev[i] !== undefined) return prev;
            const copia = [...prev];
            copia[i] = acierto;
            return copia;
        });
        setFlame(f => acierto ? Math.min(100, f + FLAME_HIT) : Math.max(0, f + FLAME_MISS));
        setEffect(acierto ? 'hit' : 'miss');
        setTimeout(() => setEffect(null), 700);
        setLeyendo(true);
    };

    //  ── OTRA VEZ ESE TROZO ────────────────────────────────────────────
    //  Rob: «si repiten y llegan a la pregunta, ya no pueden repetir otra vez y
    //  deben contestar».
    //
    //  Vuelve al PRINCIPIO del fragmento del que se pregunta —no «unos segundos
    //  atrás», que caen en mitad de la frase anterior— y lo deja correr hasta el
    //  mismo segundo en el que saltó la pregunta. Ni uno más: oír de más sería
    //  oír lo que todavía no toca.
    //
    //  No cuesta fuego. Castigar al que está intentando entender es enseñarle a
    //  no intentarlo: que repita es exactamente lo que se quiere que haga.
    const repiteElTrozo = () => {
        const i = qIdxRef.current;
        const q = i >= 0 ? questions[i] : null;
        if (!q || repetidas.has(i) || !playerRef.current) return;

        const desde = desdeDondeSeRepite(questions, i);
        const hasta = Number(q.trigger_seconds);
        if (!Number.isFinite(desde) || !Number.isFinite(hasta) || hasta <= desde) return;

        setRepetidas(prev => {
            const fuera = new Set(prev);
            fuera.add(i);
            return fuera;
        });
        //  `llego` dice si el salto ya aterrizó; `desde` es cuándo se pidió, para
        //  no dejar la pregunta escondida si no aterriza nunca.
        repiteRef.current = { idx: i, hasta, llego: false, desde: Date.now() };
        setRepitiendo(true);
        //  La pregunta se esconde, pero NO se cierra: `qIdx` se queda, y el
        //  reloj la devuelve al llegar al disparo.
        answeringRef.current = false;
        setAnswering(false);
        try {
            playerRef.current.seekTo(desde, true);
            playerRef.current.playVideo();
        } catch (_) { }
    };

    //  Encender el texto se acepta antes. Apagarlo, no: quitarse una ayuda no
    //  necesita que nadie te avise de nada.
    //
    //  ── Y EL VÍDEO SE PARA MIENTRAS SE LEE ────────────────────────────
    //  Rob, 14-10-2026: «el vídeo no se pausa, sigue corriendo, lo cual es
    //  incorrecto: el alumno necesita leer el aviso y aceptar o declinar, son
    //  minutos de lectura y toma de decisión».
    //
    //  Y es peor que perderse un trozo: mientras lee puede cruzarse un disparo,
    //  y entonces la pregunta sale DEBAJO del aviso, por algo que no ha oído.
    //  Se recuerda si estaba sonando, porque al cerrar hay que dejarlo como
    //  estaba —si estaba en pausa, en pausa se queda—.
    const sonabaRef = useRef(false);

    const tocaElCc = () => {
        if (cc) { setCc(false); guardaAjuste(CC_PUESTO, '0'); return; }
        if (leeAjuste(CC_ACEPTADO) === '1') { setCc(true); guardaAjuste(CC_PUESTO, '1'); return; }
        try {
            const estado = playerRef.current?.getPlayerState?.();
            sonabaRef.current = estado === 1 || estado === 3;
            if (sonabaRef.current) {
                playerRef.current.pauseVideo();
                setIsPlaying(false);
            }
        } catch (_) { sonabaRef.current = false; }
        setPidiendoCc(true);
    };

    //  Al cerrar el aviso —se acepte o no— el vídeo vuelve a como estaba. Y si
    //  mientras leía tenía una pregunta abierta, NO se reanuda: ahí manda la
    //  pregunta.
    const cierraElAviso = () => {
        setPidiendoCc(false);
        if (!sonabaRef.current || answeringRef.current || repiteRef.current) return;
        sonabaRef.current = false;
        try { playerRef.current?.playVideo(); setIsPlaying(true); } catch (_) { }
    };

    const aceptaElCc = () => {
        guardaAjuste(CC_ACEPTADO, '1');
        guardaAjuste(CC_PUESTO, '1');
        setCc(true);
        cierraElAviso();
    };

    const continuar = () => {
        const nextIdx = qIdxRef.current + 1;
        setLeyendo(false);

        if (nextIdx < questions.length) {
            const currentTrigger = questions[qIdxRef.current].trigger_seconds;
            const nextTriggerTime = questions[nextIdx].trigger_seconds;

            if (nextTriggerTime === currentTrigger) {
                // Siguiente ejercicio en la misma «caja» de tiempo: se encadena
                // sin despausar el vídeo.
                setQIdx(nextIdx);
                return;
            }
            setNext(nextTriggerTime);
            nextRef.current = nextTriggerTime;
        }
        // Si era la última, el vídeo sigue hasta el final sin más preguntas.

        setAnswering(false);
        answeringRef.current = false;
        setQIdx(-1);
        if (playerRef.current) {
            try { playerRef.current.playVideo(); } catch (_) { }
        }
    };

    const aciertos = resultados.filter(r => r === true).length;
    const respondidas = resultados.filter(r => r !== undefined).length;

    const handleVideoEnd = () => {
        if (finished) return;
        setFinished(true);
        onFinish({
            title: video.title,
            mode: 'doc',
            score: Math.round((aciertos / Math.max(1, respondidas)) * 100),
            correct: aciertos, total: respondidas,
        });
    };

    // Detectar fin de video con poll adicional
    useEffect(() => {
        const endPoll = setInterval(() => {
            if (!playerRef.current) return;
            try {
                const state = playerRef.current.getPlayerState();
                if (state === 0) { clearInterval(endPoll); handleVideoEnd(); }
            } catch (_) { }
        }, 500);
        return () => clearInterval(endPoll);
    }, [aciertos, respondidas]);

    const currentQ = qIdx >= 0 ? questions[qIdx] : null;

    //  El backend manda `exercise` con el tipo original. Si no viniera —material
    //  antiguo— se arma un quiz con los campos sueltos.
    const exercise = currentQ ? (currentQ.exercise || {
        type: 'quiz',
        question: currentQ.question,
        choices: currentQ.choices,
        correct: currentQ.correct,
        explanation: currentQ.explanation,
    }) : null;

    // Vive en `pantallaCompleta.js`: este bloque estaba copiado en tres
    // ficheros y el `catch` vacio dejaba al alumno pulsando un boton que no
    // hacia nada ni decia por que.
    const toggleFullscreen = () => { alternarPantallaCompleta(); };

    const togglePlayPause = () => {
        //  Durante una repetición tampoco: el trozo vuelve a la pregunta cuando
        //  el reloj llega al disparo, y un vídeo en pausa no llega nunca —la
        //  pregunta se quedaría escondida y sin forma de volver—.
        if (!playerRef.current || answering || repitiendo) return;
        try {
            const state = playerRef.current.getPlayerState();
            if (state === 1 || state === 3) {
                playerRef.current.pauseVideo();
                setIsPlaying(false);
            } else {
                playerRef.current.playVideo();
                setIsPlaying(true);
            }
        } catch (_) { }
    };

    //  Cuánto falta para la siguiente, de verdad. Antes esto escribía el minuto
    //  del vídeo en el que caía —«Próxima pregunta en 3:51» estando en el 3:04—
    //  que se lee como una cuenta atrás y no lo era.
    const faltan = nextTrigger !== null ? Math.max(0, nextTrigger - tiempo) : null;

    //  ── CUÁNTAS QUEDAN EN ESTA MISMA PAUSA ────────────────────────────
    //  Desde el 06-10-2026 los ejercicios de un mismo segmento comparten
    //  momento de disparo y se juegan seguidos, sin despausar: preguntan por el
    //  trozo que el alumno acaba de oír, y eso es lo que los hace servir para
    //  algo. El botón tiene que decir lo que va a hacer — «Continuar» cuando
    //  el vídeo va a seguir, y otra cosa cuando lo que viene es otra pregunta.
    const enEstaPausa = qIdx >= 0
        ? questions.filter(q => q.trigger_seconds === questions[qIdx].trigger_seconds).length
        : 0;
    const quedanEnEstaPausa = qIdx >= 0
        ? questions.slice(qIdx + 1).filter(
            q => q.trigger_seconds === questions[qIdx].trigger_seconds).length
        : 0;

    return (
        <div className={`dp-root ${effect === 'hit' ? 'effect-hit' : effect === 'miss' ? 'effect-miss' : ''} ${isPlaying ? 'is-playing' : ''}`}>

            {/* Top bar */}
            <div className="dp-topbar">
                <button className="dp-back-btn" onClick={onBack}>← Exit</button>
                <div className="dp-info">
                    <span className="dp-title">{video.title}</span>
                    <span className="dp-level">{video.level} · {video.language}</span>
                </div>
                {/* ── CC ───────────────────────────────────────────────
                    Va antes del fuego a propósito: es un ajuste del
                    entrenamiento, no una medalla. Y sólo sale si este vídeo
                    trae texto —los antiguos no—: un botón que no hace nada
                    enseña al alumno que la app no responde. */}
                {captions.length > 0 && (
                    <button
                        className={`dp-cc-btn${cc ? ' puesto' : ''}`}
                        data-prueba="dp-cc"
                        onClick={tocaElCc}
                        title={cc
                            ? 'Hide the on-screen text'
                            : 'Show the English text while you listen'}
                    >
                        CC
                    </button>
                )}
                <FlameBar flame={flame} />
                {/* Solo donde el navegador puede. En el Safari del iPhone,
                    `requestFullscreen` sobre un elemento no existe —solo sobre un
                    <video>— asi que este boton no hacia NADA y ademas se tragaba el
                    error. Un boton muerto que no explica por que enseña al alumno que
                    la app no responde, y deja de fiarse de los demas botones. */}
                {hayPantallaCompleta() && (
                    <button className="dp-back-btn" onClick={toggleFullscreen} style={{ padding: '7px 10px', marginLeft: 8 }}>
                        ⛶
                    </button>
                )}
            </div>

            {/* Player (sin fullscreen) */}
            <div className="dp-player-wrap">
                <div className="dp-player-container">
                    <div id="doc-yt-player" className="dp-player" />
                    <div className="dp-player-guard" />
                </div>
            </div>

            {/* ── EL TEXTO DE LO QUE SE ESTÁ OYENDO ───────────────────────
                Nunca cuando hay una pregunta en pantalla: ahí está el freno que
                hace que esto ayude en vez de sustituir. El renglón sale del
                transcript del propio vídeo —no se traduce nada— y se sincroniza
                con el mismo reloj que dispara los ejercicios, así que lo que se
                lee es exactamente lo que se está oyendo. */}
            {cc && !answering && (
                <div className="dp-cc-banda" data-prueba="dp-cc-banda">
                    <span className="dp-cc-texto">
                        {renglonEn(captions, tiempo)?.text || '·'}
                    </span>
                </div>
            )}

            {/* ── POR DÓNDE VA EL VÍDEO Y DÓNDE CAEN LAS PREGUNTAS ──────────
                Rob: «no sabemos cómo vamos, o cuánto falta, o dónde está a
                punto de salir un ejercicio». La barra es el vídeo entero; cada
                marca es una pregunta, puesta en su minuto y pintada según cómo
                haya ido. No se puede arrastrar a propósito: saltar adelante
                sería saltarse los ejercicios. */}
            <ProgresoDelVideo
                tiempo={tiempo}
                duracion={duracion}
                preguntas={questions}
                resultados={resultados}
                indiceActual={qIdx}
            />

            {/* Progress chips */}
            <div className="dp-questions-progress">
                {questions.map((_, i) => {
                    const r = resultados[i];
                    const cls = r === true ? 'bien' : r === false ? 'mal' : i === qIdx ? 'active' : '';
                    return (
                        <div key={i} className={`dp-q-dot ${cls}`}
                             title={r === true ? 'Correct' : r === false ? 'Wrong' : 'Not yet'}>
                            {r === true ? '✓' : r === false ? '✕' : i + 1}
                        </div>
                    );
                })}
            </div>

            {/* Exercise overlay */}
            {answering && exercise && (
                <div className="dp-exercise-overlay">
                    <div className="dp-exercise-card">
                        <div className="dp-q-label">
                            Question {qIdx + 1} of {questions.length}
                            {enEstaPausa > 1 && (
                                <span className="dp-q-label-bloque">
                                    · {enEstaPausa - quedanEnEstaPausa} of {enEstaPausa} at this point
                                </span>
                            )}
                        </div>
                        <ExerciseWidget
                            key={`ex-${qIdx}`}
                            exercise={exercise}
                            onResuelto={alResolver}
                        />
                        {/* ── OTRA VEZ ESE TROZO ──────────────────────────
                            Rob: «cuando llegan a la pregunta y no les queda
                            claro, deberíamos tener una oportunidad de repetir
                            esa parte del vídeo... si repiten y llegan a la
                            pregunta, ya no pueden repetir otra vez y deben
                            contestar».

                            Sólo ANTES de responder: después ya no es volver a
                            escuchar, es comprobar la respuesta. Y una sola vez,
                            que lo dice el propio botón cuando se gasta. */}
                        {!leyendo && desdeDondeSeRepite(questions, qIdx) !== null && (
                            <div className="dp-repite-fila">
                                {repetidas.has(qIdx) ? (
                                    <span className="dp-repite-gastado" data-prueba="dp-repite-gastado">
                                        You already replayed this part · time to answer
                                    </span>
                                ) : (
                                    <button
                                        className="dp-repite-btn"
                                        data-prueba="dp-repite"
                                        onClick={repiteElTrozo}
                                        title={`Back to ${reloj(desdeDondeSeRepite(questions, qIdx))}`
                                            + ' — where the last question left off — and plays up to here'}
                                    >
                                        🔁 Didn't catch it? Hear it again
                                        <span className="dp-repite-nota">once only</span>
                                    </button>
                                )}
                            </div>
                        )}

                        {/* El vídeo espera aquí. Era un `setTimeout` de segundo
                            y medio: un reloj no sabe cuándo has terminado de
                            leer, y la explicación es justo lo que se quiere
                            leer cuando se ha fallado. */}
                        {leyendo && (
                            <div className="dp-continuar-fila">
                                <span className="dp-continuar-nota">
                                    {quedanEnEstaPausa > 0
                                        ? `Still paused: ${quedanEnEstaPausa === 1
                                            ? 'one more question'
                                            : `${quedanEnEstaPausa} more questions`} from this same point.`
                                        : 'The video is paused. Carry on when you are ready.'}
                                </span>
                                <button className="dp-continuar-btn" onClick={continuar} autoFocus>
                                    {quedanEnEstaPausa > 0 ? 'Next question →' : 'Continue ▶'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Aquí estuvo un cartel flotante que decía «Escuchando otra vez»
                mientras se repetía el trozo. Rob lo quitó el 14-10-2026: «eso es
                basura, elimínalo, ya decidí verlo de nuevo; ese mensaje flotante
                sólo roba espacio en pantalla y distrae en vez de que el alumno
                ponga atención ahí».

                Tenía razón: el aviso se escribió para que no pareciera un fallo
                que la pregunta desapareciera sola, pero NO desaparece sola —la
                acaba de apartar él—. Avisar de algo que uno mismo acaba de hacer
                es ruido encima del vídeo que ha pedido volver a ver. */}

            {/* ── ANTES DE ENCENDER EL TEXTO ───────────────────────────────
                Rob, con estas palabras: «este es un entrenamiento de oído y
                comprensión; activar esta función es para alumnos básicos que
                batallan al oír. Si tú eres nivel B1 en adelante te recomiendo no
                usar esta función y entrenar tu oído».

                No se esconde el botón ni se le prohibe nada a nadie: se le dice
                para quién es, y decide él. Un alumno al que se le niega una
                ayuda cierra la app; uno al que se le explica, la usa menos. */}
            {pidiendoCc && (
                <div className="dp-cc-aviso-fondo" data-prueba="dp-cc-aviso">
                    <div className="dp-cc-aviso">
                        <div className="dp-cc-aviso-tit">Antes de encender el texto</div>
                        <p className="dp-cc-aviso-txt">
                            Esto es un <strong>entrenamiento de oído y comprensión</strong>.
                        </p>
                        <p className="dp-cc-aviso-txt">
                            El texto en inglés es para alumnos básicos que batallan al oír:
                            ata lo que suena con las palabras que ves. Si eres
                            {' '}<strong>B1 en adelante</strong>, te recomiendo no usarlo y
                            entrenar el oído.
                        </p>
                        <p className="dp-cc-aviso-txt tenue">
                            Siempre en inglés, nunca traducido. Y desaparece cuando sale una
                            pregunta: la respuesta la pones tú.
                        </p>
                        <div className="dp-cc-aviso-btns">
                            <button className="dp-cc-si" data-prueba="dp-cc-acepto"
                                    onClick={aceptaElCc}>
                                Acepto, enciéndelo
                            </button>
                            <button className="dp-cc-no" data-prueba="dp-cc-declino"
                                    onClick={cierraElAviso}>
                                Declinar · entreno sin texto
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Score */}
            <div className="dp-score-bar">
                <span className="dp-score-lado">
                    ✅ {aciertos} of {respondidas} answered
                    <span className="dp-score-total"> · {questions.length} in total</span>
                </span>

                <div className="dp-score-centro">
                    {!answering && (
                        <button className="dp-play-btn" onClick={togglePlayPause}
                                title={isPlaying ? 'Pause' : 'Play'}>
                            {isPlaying ? '⏸' : '▶'}
                        </button>
                    )}
                </div>

                <span className="dp-score-lado dp-score-lado--der">
                    {faltan === null
                        ? (respondidas >= questions.length ? 'No questions left' : 'Getting ready…')
                        : <>Next question in <strong>{reloj(faltan)}</strong></>}
                </span>
            </div>
        </div>
    );
}

/* ── LA BARRA DEL VÍDEO ──────────────────────────────────────────────────── */
function ProgresoDelVideo({ tiempo, duracion, preguntas, resultados, indiceActual }) {
    //  Sin duración no se puede situar nada. Pasa durante el primer segundo,
    //  mientras YouTube arranca: se enseña la barra vacía en vez de marcas
    //  amontonadas en el cero, que es lo que haría dividir entre uno.
    const hayEscala = duracion > 0;
    const pct = hayEscala ? Math.min(100, (tiempo / duracion) * 100) : 0;

    return (
        <div className="dp-linea">
            <span className="dp-linea-reloj">{reloj(tiempo)}</span>
            <div className="dp-linea-pista">
                <div className="dp-linea-hecho" style={{ width: `${pct}%` }} />
                {hayEscala && preguntas.map((q, i) => {
                    const x = Math.min(100, (q.trigger_seconds / duracion) * 100);
                    const r = resultados[i];
                    const cls = r === true ? 'bien' : r === false ? 'mal'
                        : i === indiceActual ? 'ahora' : '';
                    return (
                        <span
                            key={i}
                            className={`dp-linea-marca ${cls}`}
                            style={{ left: `${x}%` }}
                            title={`Question ${i + 1} · ${reloj(q.trigger_seconds)}`}
                        />
                    );
                })}
                <div className="dp-linea-cabeza" style={{ left: `${pct}%` }} />
            </div>
            <span className="dp-linea-reloj">{hayEscala ? reloj(duracion) : '--:--'}</span>
        </div>
    );
}

function FlameBar({ flame }) {
    const size = 0.7 + (flame / 100) * 1.1;
    const color = flame > 70 ? '#f97316' : flame > 40 ? '#fb923c' : '#94a3b8';
    return (
        <div className="mp-flame-wrap">
            <span className="mp-flame-emoji" style={{ transform: `scale(${size})` }}>🔥</span>
            <div className="mp-flame-bar-track">
                <div className="mp-flame-bar-fill" style={{ width: `${flame}%`, background: color }} />
            </div>
            <span className="mp-flame-pct">{flame}%</span>
        </div>
    );
}
